import cn from 'classnames';
import { getCheckoutLink } from 'components/Basket/selector';
import { Button } from 'components/Button';
import { getContainsOnlyRentalProducts } from 'components/Cart/selector';
import { getHoverContainer } from 'components/Pagelayout/PageLayout';
import Drawer from 'components/Shared/Drawer';
import Modal from 'components/Shared/Modal';
import { Title } from 'components/Shared/Product';
import { isMobile } from 'components/Shared/responsive';
import { AddItemResponse } from 'models/AddItemResponse';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { hasElements, NO_ELEMENT, rm } from 'utils';
import { getQaDataAttr } from '@bygghemmait/utilities';
import styles from './addToCartHover.scss';
import { AccessoryItemList } from 'views/ProductPage/themes/BGH/addToCartHover/accessoryItemList';
import { getAddonTitles, getProductTitleProps } from 'views/ProductPage/selector';

const productTitleClassNames = {
  innerWrapper: styles.productTitle
};

interface AddToCartHoverProps
  extends Partial<Pick<AddItemResponse, 'addItemRecommendations' | 'message'>> {
  isOpen: boolean;
  close: () => void;
}

export const AddToCartHover = ({
  isOpen,
  close,
  addItemRecommendations,
  message
}: AddToCartHoverProps) => {
  const mobile = useSelector(isMobile);
  const productTitle = useSelector(getProductTitleProps);
  const addonTitles = useSelector(getAddonTitles);
  const checkoutLink = useSelector(getCheckoutLink);
  const onlyContainsRentalProducts = useSelector(getContainsOnlyRentalProducts);

  const hasHero = !!addItemRecommendations?.hero;
  const hasCartRecommendations = hasElements(addItemRecommendations?.recommendationBlocks);

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  if (!(hasHero || hasCartRecommendations) || onlyContainsRentalProducts) {
    return NO_ELEMENT;
  }

  const content = (desktop?: boolean) => (
    <div
      className={cn(styles.addToCartHover, {
        [styles.desktop]: desktop
      })}
    >
      <div className={styles.header}>
        <div className={styles.headerTitle}>{rm('cart_hover_header_title')}</div>
        {productTitle && (
          <Title
            classNames={productTitleClassNames}
            title={productTitle.title}
            url={productTitle.url}
            titleSplitPos={productTitle.titleSplitPos}
            ticket={productTitle.ticket}
          />
        )}
        {hasElements(addonTitles) && (
          <div className={cn(styles.headerTitle, styles.addonHeaderTitle)}>
            {rm('cart_hover_addon_header_title')}:
          </div>
        )}
        {addonTitles.map(addon => (
          <div key={addon}>{addon}</div>
        ))}
        {message && <div className={styles.message}>{message}</div>}
      </div>

      <div className={styles.body}>
        {addItemRecommendations?.hero && (
          <AccessoryItemList
            title={rm('cart_hover_section_title_necessary')}
            products={[addItemRecommendations.hero]}
            setDrawerState={setIsDrawerOpen}
          />
        )}

        {hasCartRecommendations &&
          addItemRecommendations?.recommendationBlocks.map(block => (
            <AccessoryItemList
              key={block.title}
              title={block.title}
              products={block.recommendProducts}
              setDrawerState={setIsDrawerOpen}
            />
          ))}
      </div>

      <div className={styles.footer}>
        <Button
          className={styles.closeButton}
          appearance="tertiary"
          onClick={close}
          {...getQaDataAttr('addtocart-modal-close')}
        >
          {rm('cart_hover_button_back')}
        </Button>
        <Button
          href={checkoutLink}
          className={styles.checkoutButton}
          appearance="quaternary"
          textTransform="uppercase"
          {...getQaDataAttr('addtocart-modal-checkout')}
        >
          {rm('cart_hover_button_cart')}
        </Button>
      </div>
    </div>
  );

  return mobile ? (
    <Drawer
      isOpen={isOpen}
      width="full"
      transparent
      padding="none"
      side="right"
      container={getHoverContainer}
    >
      {content()}
    </Drawer>
  ) : (
    <Modal
      containerSelector={getHoverContainer}
      onClose={close}
      contentClassName={styles.modal}
      show={isOpen}
      overlayClassName={cn({
        [styles.drawerOpen]: isDrawerOpen
      })}
    >
      {content(true)}
    </Modal>
  );
};
